/**
 * SaaS Admin — gobernanza de templates email por tenant (Communication Governance B10).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TenantPicker, type TenantPickerOption } from "@/components/admin/TenantPicker";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { EMAIL_CATEGORY_LABELS, type EmailTemplateCategory, type EmailTemplateDef } from "@/lib/email-template-registry";
import type { EmailTemplateAccess } from "@/lib/email-template-governance";

type TenantOption = TenantPickerOption;

type Payload = {
  catalog: EmailTemplateDef[];
  overrides: Record<string, boolean>;
  access: Record<string, EmailTemplateAccess>;
  planId: string;
};

export function AdminEmailGatesPanel() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState<string>("");
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    apiGetJson<TenantOption[] | { tenants: TenantOption[] }>("/api/admin/tenants")
      .then((r) =>
        setTenants(
          (Array.isArray(r) ? r : (r.tenants ?? [])).map((t) => ({
            id: t.id,
            name: t.name,
            slug: t.slug,
            productFlags: t.productFlags,
          })),
        ),
      )
      .catch((e) => toast.error(e instanceof Error ? e.message : "Error tenants"));
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await apiGetJson<Payload>(
        `/api/admin/tenant-email-gates?tenantId=${encodeURIComponent(id)}`,
      );
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar gates");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId) void load(tenantId);
  }, [tenantId, load]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byCategory = new Map<EmailTemplateCategory, EmailTemplateDef[]>();
    for (const m of data.catalog) {
      const arr = byCategory.get(m.category) ?? [];
      arr.push(m);
      byCategory.set(m.category, arr);
    }
    return Array.from(byCategory.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const patch = async (key: string, value: boolean | null) => {
    if (!tenantId) return;
    setSaving(key);
    try {
      await apiSendJson<{ overrides: Record<string, boolean> }>(
        "/api/admin/tenant-email-gates",
        "PATCH",
        { tenantId, key, value },
      );
      await load(tenantId);
      toast.success("Override actualizado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Email · gates (capabilities)</h2>
        <p className="text-sm text-muted-foreground">
          Cada template tenant se gobierna como feature flag: plan mínimo + flag de producto + override.
          Platform siempre ON. Push se despacha post-email si el template declara push y hay destinatario.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <TenantPicker tenants={tenants} value={tenantId} onChange={setTenantId} />
        {data ? (
          <span className="text-xs text-muted-foreground">Plan efectivo: {data.planId}</span>
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : null}

      {data && !loading ? (
        <>
          {grouped.map(([cat, catRows]) => (
            <div key={cat} className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {EMAIL_CATEGORY_LABELS[cat]} <span className="font-normal">({catRows.length})</span>
              </h3>

              {/* Cards en mobile — gating separado en Elegibilidad / Entrega */}
              <div className="flex flex-col gap-2 md:hidden">
                {catRows.map((m) => {
                  const acc = data.access[m.key];
                  const ov = data.overrides[m.key];
                  const isPlatform = m.scope === "platform";
                  return (
                    <div key={m.key} className="rounded-xl border border-border p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{m.label}</p>
                          <p className="text-[11px] text-muted-foreground break-all">{m.key}</p>
                        </div>
                        {acc?.enabled ? (
                          <span className="shrink-0 text-xs text-emerald-600">ON</span>
                        ) : (
                          <span className="shrink-0 text-xs text-muted-foreground">OFF</span>
                        )}
                      </div>

                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Elegibilidad</p>
                      <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Scope</dt>
                        <dd>{m.scope}</dd>
                        <dt className="text-muted-foreground">Min plan</dt>
                        <dd>{m.minPlan ?? "—"}</dd>
                        <dt className="text-muted-foreground">Flag</dt>
                        <dd className="break-all">{m.requiredFlag ?? "—"}</dd>
                      </dl>

                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Entrega</p>
                      <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <dt className="text-muted-foreground">Canales</dt>
                        <dd>{(m.channels ?? ["email"]).join(", ")}</dd>
                        <dt className="text-muted-foreground">Override</dt>
                        <dd>{ov === true ? "force ON" : ov === false ? "force OFF" : "—"}</dd>
                      </dl>

                      {!acc?.enabled && acc?.reason ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">Motivo: {acc.reason}</p>
                      ) : null}
                      {!isPlatform ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          <Button size="sm" variant="outline" disabled={saving === m.key} onClick={() => void patch(m.key, true)}>
                            ON
                          </Button>
                          <Button size="sm" variant="outline" disabled={saving === m.key} onClick={() => void patch(m.key, false)}>
                            OFF
                          </Button>
                          <Button size="sm" variant="ghost" disabled={saving === m.key} onClick={() => void patch(m.key, null)}>
                            Reset
                          </Button>
                        </div>
                      ) : (
                        <p className="mt-2 text-[11px] text-muted-foreground">n/a</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Tabla en desktop — encabezado de dos niveles: Elegibilidad / Entrega */}
              <div className="hidden rounded-md border border-border md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      <th className="p-2" rowSpan={2}>Template</th>
                      <th className="p-2 text-center border-l border-border/60" colSpan={3}>Elegibilidad</th>
                      <th className="p-2 text-center border-l border-border/60" colSpan={3}>Entrega</th>
                      <th className="p-2" rowSpan={2}>Acciones</th>
                    </tr>
                    <tr className="text-[11px] text-muted-foreground">
                      <th className="p-1.5 font-medium border-l border-border/60">Scope</th>
                      <th className="p-1.5 font-medium">Min plan</th>
                      <th className="p-1.5 font-medium">Flag</th>
                      <th className="p-1.5 font-medium border-l border-border/60">Canales</th>
                      <th className="p-1.5 font-medium">Estado</th>
                      <th className="p-1.5 font-medium">Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catRows.map((m) => {
                      const acc = data.access[m.key];
                      const ov = data.overrides[m.key];
                      const isPlatform = m.scope === "platform";
                      return (
                        <tr key={m.key} className="border-t border-border/60">
                          <td className="p-2">
                            <div className="font-medium">{m.label}</div>
                            <div className="text-[11px] text-muted-foreground">{m.key}</div>
                          </td>
                          <td className="p-2 text-muted-foreground border-l border-border/40">{m.scope}</td>
                          <td className="p-2">{m.minPlan ?? "—"}</td>
                          <td className="p-2 text-[11px]">{m.requiredFlag ?? "—"}</td>
                          <td className="p-2 text-[11px] border-l border-border/40">{(m.channels ?? ["email"]).join(", ")}</td>
                          <td className="p-2">
                            {acc?.enabled ? (
                              <span className="text-emerald-600">ON</span>
                            ) : (
                              <span className="text-muted-foreground">OFF ({acc?.reason})</span>
                            )}
                          </td>
                          <td className="p-2">
                            {ov === true ? "force ON" : ov === false ? "force OFF" : "—"}
                          </td>
                          <td className="p-2">
                            {isPlatform ? (
                              <span className="text-[11px] text-muted-foreground">n/a</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                <Button size="sm" variant="outline" disabled={saving === m.key} onClick={() => void patch(m.key, true)}>
                                  ON
                                </Button>
                                <Button size="sm" variant="outline" disabled={saving === m.key} onClick={() => void patch(m.key, false)}>
                                  OFF
                                </Button>
                                <Button size="sm" variant="ghost" disabled={saving === m.key} onClick={() => void patch(m.key, null)}>
                                  Reset
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
